import Cookies from "js-cookie";
import {
  login as authLogin,
  requestPasswordReset as authRequestPasswordReset,
  setPassword as authSetPassword,
} from "@/services/authService";
import {
  User,
  LoginResponse,
  RequestPasswordResetRequest,
  SetPasswordRequest,
} from "@/models/user.model";
import { Router } from "vue-router";
import userService from "@/services/userService";
import { COOKIE_AUTH_TOKEN, LS_USER } from "@/constants/StorageKeys";
import { ROUTE_LOGIN } from "@/constants/RouteNames";
import { Roles } from "@/constants/Roles";

type State = {
  token: string | null;
  user: User | null;
};

const loadUserFromStorage = (): User | null => {
  const stored = localStorage.getItem(LS_USER);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      localStorage.removeItem(LS_USER);
      return null;
    }
  }
  return null;
};

const state: State = {
  token: Cookies.get(COOKIE_AUTH_TOKEN) || null,
  user: loadUserFromStorage(),
};

const mutations = {
  setAuth(state: State, response: LoginResponse) {
    state.token = response.token;
    state.user = response.user;
    Cookies.set(COOKIE_AUTH_TOKEN, response.token, { sameSite: "Strict" });
    localStorage.setItem(LS_USER, JSON.stringify(response.user));
  },

  setCurrentUser(state: State, user: User | null) {
    state.user = user;
    if (user) {
      localStorage.setItem(LS_USER, JSON.stringify(user));
      return;
    }
    localStorage.removeItem(LS_USER);
  },

  clearAuth(state: State) {
    state.token = null;
    state.user = null;
    Cookies.remove(COOKIE_AUTH_TOKEN);
    localStorage.removeItem(LS_USER);
  },
};

const getters = {
  isAuthenticated: (state: State) => !!state.token,
  currentUser: (state: State) => state.user,
  userName: (state: State) => state.user?.fullName || "",
  userEmail: (state: State) => state.user?.email || "",
  userRole: (state: State) => state.user?.role ?? -1,
  userUsername: (state: State) => state.user?.username || "",
};

const actions = {
  async login(
    { commit, dispatch }: { commit: any; dispatch: any },
    { emailOrUsername, password }: { emailOrUsername: string; password: string }
  ) {
    try {
      const response = await authLogin({ emailOrUsername, password });
      if (response.user?.role === Roles.Client) {
        await dispatch("notificator/error", "errors.client-not-allowed", { root: true });
        throw new Error("client-not-allowed");
      }
      commit("setAuth", response);
      return response;
    } catch (error: any) {
      if (error?.message !== "client-not-allowed") {
        await dispatch("notificator/apiError", error, { root: true });
      }
      throw error;
    }
  },

  async setPassword(
    { dispatch }: { dispatch: any },
    request: SetPasswordRequest
  ) {
    try {
      const result = await authSetPassword(request);
      return result;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async requestPasswordReset(
    { dispatch }: { dispatch: any },
    request: RequestPasswordResetRequest
  ) {
    try {
      return await authRequestPasswordReset(request);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async loadMyProfile(
    { state, commit, dispatch }: { state: State; commit: any; dispatch: any },
    options: { notifyOnError?: boolean } = {}
  ) {
    const token = state.token || Cookies.get(COOKIE_AUTH_TOKEN);
    if (!token) return null;

    try {
      const profile = await userService.getMyProfile();
      commit("setCurrentUser", profile);
      return profile;
    } catch (error: any) {
      if (options.notifyOnError) {
        await dispatch("notificator/apiError", error, { root: true });
      }
      return null;
    }
  },

  logout({ commit }: { commit: any }, router?: Router) {
    commit("clearAuth");
    commit("operations/resetState", undefined, { root: true });
    if (router) {
      router.push({ name: ROUTE_LOGIN });
    }
  },

  checkExpiration({ state, commit }: { state: State; commit: any }) {
    const token = state.token || Cookies.get(COOKIE_AUTH_TOKEN);
    if (!token) return false;

    try {
      const parts = token.split(".");
      if (parts.length < 2) return false;

      const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const paddedPayload = payloadBase64.padEnd(
        payloadBase64.length + ((4 - (payloadBase64.length % 4)) % 4),
        "="
      );
      const payload = JSON.parse(atob(paddedPayload));
      const exp = typeof payload?.exp === "number" ? payload.exp : null;

      if (!exp) return false;
      const isExpired = Date.now() >= exp * 1000;
      if (isExpired) {
        commit("clearAuth");
        commit("operations/resetState", undefined, { root: true });
      }
      return isExpired;
    } catch {
      return false;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
